/**
 * Générateur — Il passe d'un mode de représentation d'une fonction à un autre.
 * Attendu ID : 222 | Niveau : 3ème | Domaine : Fonctions (d13, O10)
 *
 * Source doc prof (B3S1/B3S4/B3S6). Plan auto-validé 2026-07-16 (TODO §6) :
 * « verbal » (procédé verbal → images + formule), « indications » (quatre
 * formulations → un tableau), « chaine » (expression → tableau de valeurs).
 */

import { randomChoice, randInt, shuffle, tex, makeTexteTrou } from '../../../utils';
import { frNb, decFrag, tableFn, NOMS_FN } from './fonctions';

const ATTENDU_ID = 222;
const L_VERBAL = 'Du procédé verbal à la formule';
const L_INDICATIONS = 'Des phrases au tableau de valeurs';
const L_CHAINE = 'De l\'expression au tableau de valeurs';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « verbal » ═════════

const PROCEDES = [
    { texte: 'sa moitié', k: 0.5, exprTpl: (nom) => `${nom}(x) = \\dfrac{x}{{{2}}}` },
    { texte: 'son double', k: 2, exprTpl: (nom) => `${nom}(x) = {{2}}x` },
    { texte: 'son triple', k: 3, exprTpl: (nom) => `${nom}(x) = {{3}}x` },
    { texte: 'son quart', k: 0.25, exprTpl: (nom) => `${nom}(x) = \\dfrac{x}{{{4}}}` },
    { texte: 'son cinquième', k: 0.2, exprTpl: (nom) => `${nom}(x) = \\dfrac{x}{{{5}}}` },
];

const genVerbalTrous = () => {
    const nom = randomChoice(NOMS_FN);
    const proc = randomChoice(PROCEDES);
    // Deux images à résultats EXACTS : x multiples du dénominateur si besoin.
    const den = proc.k < 1 ? Math.round(1 / proc.k) : 1;
    let a; let b;
    do { a = den * randInt(2, 12); b = den * randInt(2, 12); } while (a === b);
    const phrase = `$\\text{Image de } ${a} \\text{ : } {{${frNb(a * proc.k)}}} \\qquad`
        + ` \\text{image de } ${b} \\text{ : } {{${frNb(b * proc.k)}}}$\n`
        + `$${proc.exprTpl(nom)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>La fonction ${nom} associe à tout nombre ${proc.texte}.</p>`
        + '<p><b>Calcule les deux images, puis complète l\'expression algébrique.</b></p>',
        { ...opts(L_VERBAL), mode: 'saisie' });
    payload._meta.params = { nom, proc: proc.texte, a, b };
    return payload;
};

// ═════════ « indications » — quatre formulations → un tableau ═════════

const genIndicationsTrous = () => {
    const nom = randomChoice(NOMS_FN);
    // 4 couples (xi, yi) à x distincts, décrits par 4 formulations différentes.
    let xs; let ys;
    for (let essai = 0; essai < 60; essai++) {
        xs = shuffle([-10, -6, -4, -2, 2, 4, 5, 8]).slice(0, 4);
        ys = shuffle([-6, -3, 2, 3, 5.5, 7, 9]).slice(0, 4);
        if (!xs.some(x => ys.includes(x))) break;
    }
    const [x1, x2, x3, x4] = xs; const [y1, y2, y3, y4] = ys;
    const indications = [
        `l'image de ${frNb(x1)} par ${nom} est ${frNb(y1)}`,
        tex(`${nom} : ${decFrag(x2)} \\mapsto ${decFrag(y2)}`),
        tex(`${nom}(${decFrag(x3)}) = ${decFrag(y3)}`),
        `un antécédent de ${frNb(y4)} par ${nom} est ${frNb(x4)}`,
    ];
    // Tableau trié par x croissant, la ligne des images en trous.
    const ordre = [0, 1, 2, 3].sort((i, j) => xs[i] - xs[j]);
    const phrase = `$${ordre.map(i => `${nom}(${decFrag(xs[i])}) = {{${frNb(ys[i])}}}`).join(' \\qquad ')}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Indications : ${indications.join(' ; ')}.</p>`
        + tableFn(nom, ordre.map(i => frNb(xs[i])), ordre.map(() => '?'))
        // Certaines images sont décimales (5,5) : la contrainte d'écriture est
        // obligatoire (§0.1), mais elle se dit en phrase.
        + '<p><b>Complète la ligne des images du tableau, dans l\'ordre croissant des x.</b> '
        + 'Écris les nombres décimaux avec une virgule.</p>',
        { ...opts(L_INDICATIONS), mode: 'saisie' });
    payload._meta.params = { nom, xs, ys };
    return payload;
};

// ═════════ « chaine » — expression → tableau ═════════

const genChaineTrous = () => {
    const nom = randomChoice(NOMS_FN);
    // f(x) = x² + bx + c (b pair, c entier) évaluée sur 3 entiers.
    const b = randomChoice([-4, -2, 0, 2, 4]);
    const c = randInt(-5, 5);
    const xs = shuffle([-2, -1, 0, 1, 2, 3, 4]).slice(0, 3).sort((u, v) => u - v);
    const f = (x) => x * x + b * x + c;
    const expr = `x^{2}${b === 0 ? '' : (b > 0 ? ` + ${b}x` : ` - ${-b}x`)}${c === 0 ? '' : (c > 0 ? ` + ${c}` : ` - ${-c}`)}`;
    const phrase = `$${xs.map(x => `${nom}(${decFrag(x)}) = {{${frNb(f(x))}}}`).join(' \\qquad ')}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`${nom}(x) = ${expr}`)}.</p>`
        // « attention aux carrés des négatifs » nommait l'erreur à éviter.
        + '<p><b>Calcule les valeurs demandées.</b></p>',
        { ...opts(L_CHAINE), mode: 'saisie' });
    payload._meta.params = { nom, b, c, xs };
    return payload;
};

const GENERATORS = {
    verbal_texte_trou_saisie: genVerbalTrous,
    indications_texte_trou_saisie: genIndicationsTrous,
    chaine_texte_trou_saisie: genChaineTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'verbal', label: L_VERBAL, formatsList: buildFormats('verbal') },
    { id: 'indications', label: L_INDICATIONS, formatsList: buildFormats('indications') },
    { id: 'chaine', label: L_CHAINE, formatsList: buildFormats('chaine') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
